import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adminEventsService } from '@/features/ticketing/services/admin.events.service'
import {
  getParticipantPackageAdminContextFn,
  lockParticipantRosterFn,
  openParticipantPublicSalesFn,
  promoteNextWaitlistedFn,
  type ParticipantPackageAdminContext,
} from '@/lib/firebase/functions'
import type { EventDocument, ProductionDocument, TicketTypeDocument } from '@/types'
import type {
  ParticipantPackageConfig,
  ParticipantPerformanceOption,
  ParticipantRosterState,
} from '@/types/content.types'
import { adminProductionsService } from '../services/adminProductions.service'
import { createUserFacingError } from '@/lib/errors'

interface Props {
  productionId: string
  production: ProductionDocument
  onSaved: () => void
}

interface PerformanceRow {
  performanceId: string
  eventId: string
  ticketTypeId: string
}

const DEFAULT_PAYMENT_WINDOW_DAYS = 14
const DEFAULT_PAYMENT_CUTOFF_DAYS = 14
const DEFAULT_TIME_ZONE = 'America/New_York'
const DEFAULT_POLICY_VERSION = 'participant-package-non-refundable-v1'
const DEFAULT_POLICY =
  'I understand that this participant package is non-refundable, including if I withdraw or cannot participate. Withdrawing forfeits my participant spot, but my purchased admission tickets remain valid.'

export function ParticipantPackageSettingsCard({ productionId, production, onSaved }: Props) {
  const packageConfig = isParticipantPackageConfig(production.participantPackage)
    ? production.participantPackage
    : undefined
  const [price, setPrice] = useState(((packageConfig?.priceCents ?? 0) / 100).toFixed(2))
  const [currency] = useState(packageConfig?.currency ?? 'usd')
  const [includedTickets, setIncludedTickets] = useState(
    String(packageConfig?.includedTicketQuantity ?? 1),
  )
  const [participantSlots, setParticipantSlots] = useState(
    String(production.intendedParticipantSlots ?? 1),
  )
  const [paymentWindowDays, setPaymentWindowDays] = useState(
    String(packageConfig?.paymentWindowDays ?? DEFAULT_PAYMENT_WINDOW_DAYS),
  )
  const [paymentCutoffDays, setPaymentCutoffDays] = useState(
    String(packageConfig?.paymentCutoffDays ?? DEFAULT_PAYMENT_CUTOFF_DAYS),
  )
  const [timeZone, setTimeZone] = useState(packageConfig?.timeZone ?? DEFAULT_TIME_ZONE)
  const [policyVersion, setPolicyVersion] = useState(
    packageConfig?.nonRefundablePolicyVersion ?? DEFAULT_POLICY_VERSION,
  )
  const [policyText, setPolicyText] = useState(
    packageConfig?.nonRefundablePolicyText ?? DEFAULT_POLICY,
  )
  const [performanceOptions, setPerformanceOptions] = useState<PerformanceRow[]>(
    rowsFromPackage(packageConfig),
  )
  const [events, setEvents] = useState<Array<EventDocument & { id: string }>>([])
  const [ticketTypes, setTicketTypes] = useState<
    Record<string, Array<TicketTypeDocument & { id: string }>>
  >({})
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adminContext, setAdminContext] = useState<ParticipantPackageAdminContext | null>(null)
  const [contextLoading, setContextLoading] = useState(true)
  const [contextError, setContextError] = useState<string | null>(null)
  const [operation, setOperation] = useState<'lock' | 'open' | string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoadingEvents(true)
    adminEventsService
      .getAllEvents()
      .then(async (loadedEvents) => {
        const entries = await Promise.all(
          loadedEvents.map(
            async (event) =>
              [event.id, await adminEventsService.getTicketTypes(event.id)] as const,
          ),
        )
        if (cancelled) return
        setEvents(loadedEvents)
        setTicketTypes(Object.fromEntries(entries))
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(formatUserFacingError(loadError, 'Failed to load performances.'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const refreshAdminContext = useCallback(async () => {
    setContextLoading(true)
    setContextError(null)
    try {
      const result = await getParticipantPackageAdminContextFn({ productionId })
      setAdminContext(result.data)
    } catch (loadError: unknown) {
      setContextError(
        formatUserFacingError(loadError, 'Participant operations are unavailable.'),
      )
    } finally {
      setContextLoading(false)
    }
  }, [productionId])

  useEffect(() => {
    void refreshAdminContext()
  }, [refreshAdminContext])

  const linkedEventIds = useMemo(
    () => new Set(performanceOptions.map((row) => row.eventId)),
    [performanceOptions],
  )

  const effectiveProduction = adminContext?.production ?? production
  const contextPackage = isParticipantPackageConfig(effectiveProduction.participantPackage)
    ? effectiveProduction.participantPackage
    : undefined
  const effectivePackage = contextPackage ?? packageConfig
  const rosterState = getRosterState(effectiveProduction.participantRosterState, !!effectivePackage)
  const offers = useMemo(
    () =>
      [...(adminContext?.offers ?? [])].sort((left, right) => {
        const leftDue = left.paymentDueAt ? Date.parse(left.paymentDueAt) : Number.POSITIVE_INFINITY
        const rightDue = right.paymentDueAt ? Date.parse(right.paymentDueAt) : Number.POSITIVE_INFINITY
        return leftDue - rightDue || (left.slotId ?? '').localeCompare(right.slotId ?? '')
      }),
    [adminContext?.offers],
  )

  function updatePerformance(index: number, patch: Partial<PerformanceRow>) {
    setPerformanceOptions((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    )
  }

  function addPerformance() {
    const event = events.find((candidate) => !linkedEventIds.has(candidate.id))
    if (!event) return
    const firstTicketType = ticketTypes[event.id]?.[0]
    setPerformanceOptions((current) => [
      ...current,
      {
        performanceId: event.id,
        eventId: event.id,
        ticketTypeId: firstTicketType?.id ?? '',
      },
    ])
  }

  function removePerformance(index: number) {
    setPerformanceOptions((current) => current.filter((_, rowIndex) => rowIndex !== index))
  }

  async function save() {
    setMessage(null)
    setError(null)

    if (rosterState === 'locked' || rosterState === 'public_sales_open') {
      return setError(withErrorCode('Participant package settings cannot change after the roster is locked.', 'app/validation'))
    }

    const priceNumber = Number(price)
    const ticketCount = Number(includedTickets)
    const slotCount = Number(participantSlots)
    const windowDays = Number(paymentWindowDays)
    const cutoffDays = Number(paymentCutoffDays)

    if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
      return setError(withErrorCode('Package price must be greater than zero.', 'app/validation'))
    }
    if (!Number.isInteger(ticketCount) || ticketCount < 1) {
      return setError(withErrorCode('Included ticket quantity must be a positive whole number.', 'app/validation'))
    }
    if (!Number.isInteger(slotCount) || slotCount < 1) {
      return setError(withErrorCode('Participant slots must be a positive whole number.', 'app/validation'))
    }
    if (!Number.isInteger(windowDays) || windowDays < 1) {
      return setError(withErrorCode('Payment window must be at least one day.', 'app/validation'))
    }
    if (!Number.isInteger(cutoffDays) || cutoffDays < 0) {
      return setError(withErrorCode('Payment cutoff must be zero or more days before the first show.', 'app/validation'))
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone }).format()
    } catch {
      return setError(withErrorCode('Enter a valid IANA time zone, such as America/New_York.', 'app/validation'))
    }
    if (!policyVersion.trim() || !policyText.trim()) {
      return setError(withErrorCode('Policy version and non-refundable policy text are required.', 'app/validation'))
    }
    if (performanceOptions.length === 0) {
      return setError(withErrorCode('Link at least one performance before configuring the package.', 'app/validation'))
    }
    if (performanceOptions.some((row) => !row.eventId || !row.ticketTypeId)) {
      return setError(withErrorCode('Every performance must have an event and participant ticket type.', 'app/validation'))
    }
    if (new Set(performanceOptions.map((row) => row.eventId)).size !== performanceOptions.length) {
      return setError(withErrorCode('Each performance may only be linked once.', 'app/validation'))
    }

    let config: ParticipantPackageConfig
    try {
      config = buildPackageConfig({
        priceCents: Math.round(priceNumber * 100),
        currency,
        includedTicketQuantity: ticketCount,
        paymentWindowDays: windowDays,
        paymentCutoffDays: cutoffDays,
        timeZone: timeZone.trim(),
        nonRefundablePolicyVersion: policyVersion.trim(),
        nonRefundablePolicyText: policyText.trim(),
        performanceOptions,
        events,
        ticketTypes,
      })
    } catch (buildError: unknown) {
      return setError(formatUserFacingError(buildError, 'Invalid performance mapping.'))
    }

    setSaving(true)
    try {
      await adminProductionsService.updateParticipantPackage(productionId, config, slotCount)
      setMessage('Participant package settings saved. Public sales remain closed until the roster is locked.')
      await refreshAdminContext()
      onSaved()
    } catch (saveError: unknown) {
      setError(formatUserFacingError(saveError, 'Failed to save package settings.'))
    } finally {
      setSaving(false)
    }
  }

  async function lockRoster() {
    setOperation('lock')
    setMessage(null)
    setError(null)
    try {
      const result = await lockParticipantRosterFn({ productionId })
      if (result.data.outstandingSlots.length > 0) {
        setMessage(
          withErrorCode(
            `Roster remains unresolved. Outstanding slots: ${result.data.outstandingSlots.join(', ')}.`,
            'participant-package/roster-unresolved',
          ),
        )
      } else {
        setMessage('Participant roster locked. Public sales are still closed.')
      }
      await refreshAdminContext()
      onSaved()
    } catch (operationError: unknown) {
      setError(formatUserFacingError(operationError, 'Failed to lock roster.'))
    } finally {
      setOperation(null)
    }
  }

  async function openPublicSales() {
    setOperation('open')
    setMessage(null)
    setError(null)
    try {
      await openParticipantPublicSalesFn({ productionId })
      setMessage('Public sales opened for the mapped performance events.')
      await refreshAdminContext()
      onSaved()
    } catch (operationError: unknown) {
      setError(formatUserFacingError(operationError, 'Failed to open public sales.'))
    } finally {
      setOperation(null)
    }
  }

  async function promoteNext(slotId: string) {
    setOperation(`promote:${slotId}`)
    setMessage(null)
    setError(null)
    try {
      const result = await promoteNextWaitlistedFn({ productionId, slotId })
      setMessage(
        result.data.submissionId
          ? `Next waitlisted applicant invited for ${slotId}.`
          : `Promotion for ${slotId}: ${formatOfferStatus(result.data.kind)}.`,
      )
      await refreshAdminContext()
      onSaved()
    } catch (operationError: unknown) {
      setError(formatUserFacingError(operationError, 'Failed to promote waitlisted applicant.'))
    } finally {
      setOperation(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Participant Package</CardTitle>
            <CardDescription>
              Configure the flat participant price, shared ticket capacity, and payment deadline policy.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={effectivePackage ? 'default' : 'outline'}>
              {effectivePackage ? 'Configured' : 'Unconfigured'}
            </Badge>
            <Button asChild type="button" variant="outline" size="sm">
              <Link to="/admin/events">Manage events &amp; tickets</Link>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
          {effectivePackage ? (
            <>
              This package charges participants a non-refundable flat amount. Their payment deadline is
              calculated from the invitation date, capped by the configured cutoff before the first show.
            </>
          ) : (
            <>
              No package is configured yet. Saving a valid package creates the participant payment path;
              until then, participant offers cannot collect payment and mapped public sales remain closed.
            </>
          )}
          <span className="mt-1 block">
            Package tax behavior is inclusive and automatic tax is always enabled. The backend currently
            has no disable-package callable, so this UI never writes an invalid empty configuration.
          </span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Flat package price" htmlFor="participant-package-price">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                id="participant-package-price"
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                className="pl-7"
              />
            </div>
          </Field>
          <Field label="Currency" htmlFor="participant-package-currency">
            <Input id="participant-package-currency" value={currency.toUpperCase()} disabled />
          </Field>
          <Field label="Included tickets" htmlFor="participant-ticket-count">
            <Input
              id="participant-ticket-count"
              type="number"
              min={1}
              value={includedTickets}
              onChange={(event) => setIncludedTickets(event.target.value)}
            />
          </Field>
          <Field label="Participant slots" htmlFor="participant-slot-count">
            <Input
              id="participant-slot-count"
              type="number"
              min={1}
              value={participantSlots}
              onChange={(event) => setParticipantSlots(event.target.value)}
            />
          </Field>
          <Field label="Days to pay (default 14)" htmlFor="participant-payment-window">
            <Input
              id="participant-payment-window"
              type="number"
              min={1}
              value={paymentWindowDays}
              onChange={(event) => setPaymentWindowDays(event.target.value)}
            />
          </Field>
          <Field label="Cutoff before first show" htmlFor="participant-payment-cutoff">
            <Input
              id="participant-payment-cutoff"
              type="number"
              min={0}
              value={paymentCutoffDays}
              onChange={(event) => setPaymentCutoffDays(event.target.value)}
            />
          </Field>
          <Field label="Production time zone" htmlFor="participant-time-zone">
            <Input
              id="participant-time-zone"
              value={timeZone}
              onChange={(event) => setTimeZone(event.target.value)}
              placeholder={DEFAULT_TIME_ZONE}
            />
          </Field>
          <Field label="Tax behavior" htmlFor="participant-tax-behavior">
            <Input id="participant-tax-behavior" value="Inclusive" disabled />
          </Field>
          <Field label="Automatic tax" htmlFor="participant-automatic-tax">
            <Input id="participant-automatic-tax" value="Enabled" disabled />
          </Field>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Performance mappings</Label>
              <p className="text-xs text-muted-foreground">
                Participant tickets draw from that performance's show capacity, shared with public sales.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addPerformance}
              disabled={
                loadingEvents ||
                linkedEventIds.size >= events.length ||
                rosterState === 'locked' ||
                rosterState === 'public_sales_open'
              }
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add performance
            </Button>
          </div>

          {loadingEvents ? (
            <div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading events and ticket types…
            </div>
          ) : performanceOptions.length === 0 ? (
            <div className="space-y-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              <p>
                No performances linked. Create each performance as a draft event, add its ticket
                types, then return here to map shared participant capacity before publishing it.
              </p>
              <Button asChild type="button" variant="outline" size="sm">
                <Link to={`/admin/events/new?productionId=${encodeURIComponent(productionId)}`}>
                  Create a performance event
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {performanceOptions.map((row, index) => {
                const availableTicketTypes = ticketTypes[row.eventId] ?? []
                return (
                  <div
                    key={`${row.performanceId}:${index}`}
                    className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_auto]"
                  >
                    <Select
                      value={row.eventId}
                      onValueChange={(eventId) =>
                        updatePerformance(index, {
                          eventId,
                          performanceId: eventId,
                          ticketTypeId: ticketTypes[eventId]?.[0]?.id ?? '',
                        })
                      }
                      disabled={rosterState === 'locked' || rosterState === 'public_sales_open'}
                    >
                      <SelectTrigger aria-label={`Performance ${index + 1}`}>
                        <SelectValue placeholder="Select performance" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map((event) => (
                          <SelectItem
                            key={event.id}
                            value={event.id}
                            disabled={event.id !== row.eventId && linkedEventIds.has(event.id)}
                          >
                            {event.title} · {formatEventDate(event.eventDate)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={row.ticketTypeId || undefined}
                      onValueChange={(ticketTypeId) => updatePerformance(index, { ticketTypeId })}
                      disabled={rosterState === 'locked' || rosterState === 'public_sales_open'}
                    >
                      <SelectTrigger aria-label={`Participant ticket type ${index + 1}`}>
                        <SelectValue placeholder="Select ticket type" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTicketTypes.map((ticketType) => (
                          <SelectItem key={ticketType.id} value={ticketType.id}>
                            {ticketType.name} · {ticketType.capacity} capacity
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removePerformance(index)}
                      disabled={rosterState === 'locked' || rosterState === 'public_sales_open'}
                      aria-label={`Remove performance ${index + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-[16rem_1fr]">
          <Field label="Non-refundable policy version" htmlFor="participant-policy-version">
            <Input
              id="participant-policy-version"
              value={policyVersion}
              onChange={(event) => setPolicyVersion(event.target.value)}
            />
          </Field>
          <Field label="Required non-refundable acknowledgment" htmlFor="participant-policy-text">
            <Textarea
              id="participant-policy-text"
              rows={4}
              value={policyText}
              onChange={(event) => setPolicyText(event.target.value)}
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted/40 p-3 text-sm">
          <div>
            <span className="text-muted-foreground">Roster state: </span>
            <strong>{rosterState ? formatRosterState(rosterState) : 'Unconfigured'}</strong>
            <span className="ml-2 text-xs text-muted-foreground">
              {rosterState === 'public_sales_open'
                ? 'Public sales are open.'
                : 'Public sales stay closed until the roster is locked and opened.'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void lockRoster()}
              disabled={!effectivePackage || rosterState !== 'unresolved' || !!operation || contextLoading}
            >
              {operation === 'lock' && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Lock roster
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void openPublicSales()}
              disabled={!effectivePackage || rosterState !== 'locked' || !!operation || contextLoading}
            >
              {operation === 'open' && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Open public sales
            </Button>
          </div>
        </div>

        {adminContext && effectivePackage && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium">Participant offer operations</h3>
                <p className="text-xs text-muted-foreground">
                  Payment status and deadlines are loaded from the admin-only callable context.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void refreshAdminContext()}
                disabled={contextLoading}
              >
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${contextLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[40rem] text-sm">
                <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Slot</th>
                    <th className="px-3 py-2 font-medium">Applicant</th>
                    <th className="px-3 py-2 font-medium">Offer</th>
                    <th className="px-3 py-2 font-medium">Payment due</th>
                    <th className="px-3 py-2 font-medium text-right">Operations</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {offers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-center text-xs text-muted-foreground">
                        No participant offer records have been issued for this production.
                      </td>
                    </tr>
                  ) : offers.map((offer) => {
                    const vacant =
                      offer.status === 'promotion_blocked_capacity' ||
                      offer.status === 'promotion_blocked_cutoff' ||
                      offer.status === 'declined' ||
                      offer.status === 'payment_forfeited' ||
                      offer.status === 'withdrawn'
                    const promoteBusy = operation === `promote:${offer.slotId}`
                    return (
                      <tr key={offer.submissionId}>
                        <td className="px-3 py-2 font-mono text-xs">{offer.slotId}</td>
                        <td className="px-3 py-2">
                          <div>{offer.email}</div>
                          {offer.waitlistPosition !== undefined && (
                            <div className="text-xs text-muted-foreground">
                              Waitlist position {offer.waitlistPosition}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <OfferStatusBadge status={offer.status} />
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {offer.paymentDueAt ? formatAdminDate(offer.paymentDueAt) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {vacant && offer.slotId?.startsWith('slot-') ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => void promoteNext(offer.slotId)}
                              disabled={!!operation}
                            >
                              {promoteBusy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                              Promote next
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {contextError && (
          <p className="text-xs text-muted-foreground">
            Participant operations could not be loaded: {contextError}
          </p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-emerald-700 dark:text-emerald-400">{message}</p>}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || loadingEvents || rosterState === 'locked' || rosterState === 'public_sales_open'}
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save participant package
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function rowsFromPackage(config?: ParticipantPackageConfig): PerformanceRow[] {
  return (
    config?.performanceOptions.map((option) => ({
      performanceId: option.performanceId,
      eventId: option.eventId,
      ticketTypeId: option.ticketTypeId,
    })) ?? []
  )
}

function isParticipantPackageConfig(value: unknown): value is ParticipantPackageConfig {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<ParticipantPackageConfig>
  return (
    Number.isInteger(candidate.priceCents) &&
    typeof candidate.currency === 'string' &&
    Number.isInteger(candidate.includedTicketQuantity) &&
    Number.isInteger(candidate.paymentWindowDays) &&
    Number.isInteger(candidate.paymentCutoffDays) &&
    typeof candidate.timeZone === 'string' &&
    candidate.taxBehavior === 'inclusive' &&
    candidate.automaticTax === true &&
    typeof candidate.nonRefundablePolicyVersion === 'string' &&
    typeof candidate.nonRefundablePolicyText === 'string' &&
    Array.isArray(candidate.performanceOptions)
  )
}

function buildPackageConfig({
  priceCents,
  currency,
  includedTicketQuantity,
  paymentWindowDays,
  paymentCutoffDays,
  timeZone,
  nonRefundablePolicyVersion,
  nonRefundablePolicyText,
  performanceOptions,
  events,
  ticketTypes,
}: {
  priceCents: number
  currency: string
  includedTicketQuantity: number
  paymentWindowDays: number
  paymentCutoffDays: number
  timeZone: string
  nonRefundablePolicyVersion: string
  nonRefundablePolicyText: string
  performanceOptions: PerformanceRow[]
  events: Array<EventDocument & { id: string }>
  ticketTypes: Record<string, Array<TicketTypeDocument & { id: string }>>
}): ParticipantPackageConfig {
  const mappedOptions: ParticipantPerformanceOption[] = performanceOptions.map((row) => {
    const event = events.find((candidate) => candidate.id === row.eventId)
    const ticketType = ticketTypes[row.eventId]?.find((candidate) => candidate.id === row.ticketTypeId)
    if (!event) throw createUserFacingError(`Performance event ${row.eventId} could not be loaded.`)
    if (!ticketType) throw createUserFacingError(`Ticket type for ${event.title} could not be loaded.`)
    return {
      performanceId: row.performanceId.trim() || row.eventId,
      eventId: row.eventId,
      ticketTypeId: row.ticketTypeId,
      ticketTypeName: ticketType.name,
      startsAt: event.eventDate.toDate().toISOString(),
      label: event.title,
      venue: event.venue,
    }
  })

  return {
    priceCents,
    currency: currency.toLowerCase(),
    includedTicketQuantity,
    paymentWindowDays,
    paymentCutoffDays,
    timeZone,
    taxBehavior: 'inclusive',
    automaticTax: true,
    nonRefundablePolicyVersion,
    nonRefundablePolicyText,
    performanceOptions: mappedOptions,
  }
}

function getRosterState(
  state: ParticipantRosterState | undefined,
  configured: boolean,
): ParticipantRosterState | null {
  if (!configured) return null
  if (state === 'locked' || state === 'public_sales_open') return state
  return 'unresolved'
}

function formatRosterState(state: ParticipantRosterState): string {
  if (state === 'public_sales_open') return 'Public sales open'
  if (state === 'locked') return 'Locked'
  return 'Unresolved'
}

function formatOfferStatus(status: string): string {
  if (!status) return 'Unknown'
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function OfferStatusBadge({ status }: { status: string }) {
  const blocked = status?.startsWith('promotion_blocked') ?? false
  const paid = status === 'paid'
  return (
    <Badge variant={paid ? 'default' : blocked ? 'destructive' : 'outline'}>
      {formatOfferStatus(status)}
    </Badge>
  )
}

function formatEventDate(eventDate: { toDate: () => Date }): string {
  return eventDate.toDate().toLocaleString()
}

function formatAdminDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}
